import { HttpStatusCode } from 'axios';
import { useMemo } from 'react';
import { AttachedDocument, DisputeDetail, DisputeNote, DisputeResolution, DisputeRouting, DisputeSummary, DisputeTrackingRecord } from 'types';
import type { DisputeFilterRequest } from 'types/DisputeFilterRequest';
import { useAuthState } from 'state';
import ApiService from './ApiService';

export type UploadDocumentResult = {
    data: AttachedDocument[],
    status: boolean
}

const getDisputesByFilterRequests = new Map<string, Promise<DisputeSummary[]>>();

const buildDisputeFilterRequestKey = (filter: DisputeFilterRequest) => {
    const normalized: Record<string, unknown> = {};

    Object.entries(filter)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .sort(([left], [right]) => left.localeCompare(right))
        .forEach(([key, value]) => {
            normalized[key] = value;
        });

    return JSON.stringify(normalized);
};

export class DisputeService extends ApiService {

    async addNote(disputeNumber: string, note: DisputeNote): Promise<boolean> {
        const { status } = await this.post(`/api/Disputes/${disputeNumber}/notes`, note);
        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }

    async getDisputeCategories() {
        const { data, status } = await this.get('/api/disputes/DisputeTypeCategories');
        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getDisputeDetails(disputeNumber: string): Promise<DisputeDetail | null> {
        const { data, status } = await this.get(`/api/Disputes/${disputeNumber}`); 
        return this.IsSuccessStatusCode(status) ? data : null;
    }

    async getDisputeReasons() {
        const { data, status } = await this.get('/api/DisputeReasons');
        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getAdjustmentDisputeReasons(adjustmentTypeId?: number) {
        const { data, status } = await this.get('/api/DisputeReasons/AdjustmentTypeId');
        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getMarDisputeReasons() {
        const { data, status } = await this.get('/api/DisputeReasons/MarBillingCode');

        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getMarDisputeTypes() {
        const { data, status } = await this.get('/api/DisputeReasons/MarDisputeTypes');

        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getDisputeSummaries(durationInDays: string, accountId?: string): Promise<DisputeSummary[]> {
        const { data, status } = await this.get(`/api/Disputes?retrieveDays=-${durationInDays}`
            + this.getOptionalQueryParam(false, 'accountId', accountId)
        );

        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getDisputeSummariesByFilter(filter: DisputeFilterRequest): Promise<DisputeSummary[]> {
        const requestKey = buildDisputeFilterRequestKey(filter);
        const inFlightRequest = getDisputesByFilterRequests.get(requestKey);

        if (inFlightRequest) {
            return inFlightRequest;
        }

        const request = this.post('/api/Disputes/filter', filter)
            .then(({ data, status }) => this.IsSuccessStatusCode(status) ? data : [])
            .finally(() => {
                getDisputesByFilterRequests.delete(requestKey);
            });

        getDisputesByFilterRequests.set(requestKey, request);
        return request;
    }

    async getDisputeTracking(disputeNumber: string, isPublic?: boolean): Promise<DisputeTrackingRecord[]> {
        const { data, status } = await this.get(`/api/Disputes/${disputeNumber}/tracking`
            + this.getOptionalQueryParam(true, 'IsPublic', isPublic != null ? isPublic.toString() : "")
        );

        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async getResolutionReasons() {
        const { data, status } = await this.get('/api/DisputeResolutions');
        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async reopenDispute(disputeNumber: string, data: DisputeNote) {
        const { status } = await this.put(`/api/Disputes/${disputeNumber}/reopen`, data);
        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }

    async resolveDispute(disputeNumber: string, data: DisputeResolution, files: File[]) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        files.forEach((file) => formData.append('files', file));
        const { status } = await this.put(`/api/Disputes/${disputeNumber}/resolve`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }});
        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }
    
    async routeDispute(disputeNumber: string, data: DisputeRouting) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        const { status } = await this.put(`/api/Disputes/${disputeNumber}/route`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }});
        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }
    
    async routeDisputeWithDocuments(disputeNumber: string, data: DisputeRouting, files: File[]) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        files.forEach((file) => formData.append('files', file));

        const { status } = await this.put(`/api/Disputes/${disputeNumber}/route`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }});

        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }

    async routeToDot(disputeNumber: string, inputData: DisputeRouting, files: File[]) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(inputData));
        files.forEach((file) => formData.append('files', file));
        const { data, status } = await this.put(`/api/Disputes/${disputeNumber}/reroute`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }});

        const result: UploadDocumentResult = {
            data: data,
            status: status === HttpStatusCode.Ok || status === HttpStatusCode.Created
        }
        return result;
    }

    async submitDispute(data: any, files: File[]) : Promise<any> {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        files.forEach((file) => formData.append('files', file));
        const response = await this.post('/api/Disputes', formData, { headers:{ 'Content-Type': 'multipart/form-data' }});

        // PRM duplicate claim fix (2026-08-24; QA follow-up 2026-09-11): Preserve only
        // the intentional structured 409 duplicate response. Generic server errors can
        // contain internal exception messages and must use the form's safe fallback text.
        const errorData = typeof response.data === 'string'
            ? { message: response.data }
            : response.data ?? {};
        const isDuplicateResponse = response.status === HttpStatusCode.Conflict
            && errorData.code === 'DUPLICATE_DISPUTE';

        const result = {
            disputeNumber: response.data?.disputeNumber,
            status: response.status === HttpStatusCode.Ok || response.status === HttpStatusCode.Created,
            errorCode: isDuplicateResponse ? errorData.code : undefined,
            errorMessage: isDuplicateResponse ? errorData.message : undefined,
            existingDisputeNumber: isDuplicateResponse ? errorData.existingDisputeNumber : undefined
        }

        return result
    }
    
    async uploadDocuments(disputeNumber: string, inputData: any, files: File[]) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(inputData));
        files.forEach((file) => formData.append('files,', file));
        const { data, status } = await this.post(`/api/Disputes/${disputeNumber}/documents`, formData,  { headers: {'Content-Type': 'multipart/form-data'}} );
        
        const result: UploadDocumentResult = {
            data: data,
            status: status === HttpStatusCode.Ok || status === HttpStatusCode.Created
        }

        return result;
    }
}

export const useDisputeService = () => {
    const auth = useAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => new DisputeService(), [auth]);
};
